import { type CSSProperties, type InputHTMLAttributes, forwardRef } from 'react'
import { cn } from '../lib/cn'

export type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'min' | 'max'> & {
  value: number
  min: number
  max: number
}

/**
 * Styled range input: the track fills with the primary color up to the current
 * value (consistent across browsers, unlike `accent-color`), round thumb on the
 * page background. The input box itself is the touch target (24 px, 44 px on
 * coarse pointers); the visible 6 px track is drawn by the track pseudo-element.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ className, value, min, max, style, ...props }, ref) => {
    const pct = max > min ? ((value - min) / (max - min)) * 100 : 0
    return (
      <input
        ref={ref}
        type="range"
        value={value}
        min={min}
        max={max}
        style={{ '--slider-fill': `${pct}%`, ...style } as CSSProperties}
        className={cn(
          'h-6 w-full cursor-pointer appearance-none bg-transparent outline-none pointer-coarse:h-11',
          // WebKit track (filled up to --slider-fill) + thumb centered on it
          '[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full',
          '[&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--primary)_var(--slider-fill),var(--border)_var(--slider-fill))]',
          '[&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary',
          '[&::-webkit-slider-thumb]:bg-background [&::-webkit-slider-thumb]:shadow-sm',
          '[&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110 active:[&::-webkit-slider-thumb]:scale-95',
          'focus-visible:[&::-webkit-slider-thumb]:ring-2 focus-visible:[&::-webkit-slider-thumb]:ring-ring',
          'focus-visible:[&::-webkit-slider-thumb]:ring-offset-2 focus-visible:[&::-webkit-slider-thumb]:ring-offset-background',
          // Firefox track + progress + thumb
          '[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-border',
          '[&::-moz-range-progress]:h-1.5 [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-primary',
          '[&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:bg-background [&::-moz-range-thumb]:shadow-sm',
          'focus-visible:[&::-moz-range-thumb]:ring-2 focus-visible:[&::-moz-range-thumb]:ring-ring',
          'focus-visible:[&::-moz-range-thumb]:ring-offset-2 focus-visible:[&::-moz-range-thumb]:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    )
  },
)
Slider.displayName = 'Slider'
